import { useState } from 'react';
import type { PeriodBreakdown } from '../types';

type TempoChartProps = {
  homePeriods: PeriodBreakdown[];
  awayPeriods: PeriodBreakdown[];
  homeTeamName: string;
  awayTeamName: string;
  homeDataAvailable?: boolean;
  awayDataAvailable?: boolean;
};

type ChartMode = 'scored' | 'conceded';

const PERIOD_LABELS: Record<string, string> = {
  '0-15': "0-15'",
  '16-30': "16-30'",
  '31-45': "31-45'+",
  '46-60': "46-60'",
  '61-75': "61-75'",
  '76-90': "76-90'+",
};

const periods = ['0-15', '16-30', '31-45', '46-60', '61-75', '76-90'];

// Single chart component for one mode (scored or conceded)
function SingleChart({
  homePeriods,
  awayPeriods,
  homeTeamName,
  awayTeamName,
  homeDataAvailable,
  awayDataAvailable,
  mode,
  showHeader = true,
}: {
  homePeriods: PeriodBreakdown[];
  awayPeriods: PeriodBreakdown[];
  homeTeamName: string;
  awayTeamName: string;
  homeDataAvailable: boolean;
  awayDataAvailable: boolean;
  mode: ChartMode;
  showHeader?: boolean;
}) {
  const getPercentage = (period: PeriodBreakdown | undefined, totalGoals: number): number => {
    if (!period || totalGoals === 0) return 0;
    const goals = mode === 'scored' ? period.goals_for : period.goals_against;
    return (goals / totalGoals) * 100;
  };

  const homeTotalScored = homePeriods.reduce((sum, p) => sum + p.goals_for, 0);
  const homeTotalConceded = homePeriods.reduce((sum, p) => sum + p.goals_against, 0);
  const awayTotalScored = awayPeriods.reduce((sum, p) => sum + p.goals_for, 0);
  const awayTotalConceded = awayPeriods.reduce((sum, p) => sum + p.goals_against, 0);

  const homeTotal = mode === 'scored' ? homeTotalScored : homeTotalConceded;
  const awayTotal = mode === 'scored' ? awayTotalScored : awayTotalConceded;

  const allPcts = [
    ...homePeriods.map((p) => getPercentage(p, homeTotal)),
    ...awayPeriods.map((p) => getPercentage(p, awayTotal)),
  ];
  const maxPct = Math.max(...allPcts, 20);

  return (
    <div className="tempo-chart__single">
      {showHeader && (
        <div className="tempo-chart__single-header">
          <span className="tempo-chart__team tempo-chart__team--home">{homeTeamName}</span>
          <span className="tempo-chart__mode-label">{mode === 'scored' ? 'Scored' : 'Conceded'}</span>
          <span className="tempo-chart__team tempo-chart__team--away">{awayTeamName}</span>
        </div>
      )}
      <div className="tempo-chart__grid">
        {periods.map((period, idx) => {
          const homePeriod = homePeriods.find((p) => p.period === period);
          const awayPeriod = awayPeriods.find((p) => p.period === period);
          const homePct = homeDataAvailable ? getPercentage(homePeriod, homeTotal) : 0;
          const awayPct = awayDataAvailable ? getPercentage(awayPeriod, awayTotal) : 0;
          const homeWidth = (homePct / maxPct) * 100;
          const awayWidth = (awayPct / maxPct) * 100;
          const homeGoals = mode === 'scored' ? homePeriod?.goals_for : homePeriod?.goals_against;
          const awayGoals = mode === 'scored' ? awayPeriod?.goals_for : awayPeriod?.goals_against;

          return (
            <div key={period} className={`tempo-chart__row ${idx === 3 ? 'tempo-chart__row--half-break' : ''}`}>
              <div className="tempo-chart__bar-container tempo-chart__bar-container--home">
                {homeDataAvailable ? (
                  <>
                    <span className="tempo-chart__pct">{homePct.toFixed(0)}%</span>
                    <div
                      className="tempo-chart__bar tempo-chart__bar--home"
                      style={{ width: `${homeWidth}%` }}
                      title={`${homeGoals || 0} goals ${mode}`}
                    />
                  </>
                ) : (
                  idx === 0 && <span className="tempo-chart__no-data">No data</span>
                )}
              </div>
              <div className="tempo-chart__period">{PERIOD_LABELS[period]}</div>
              <div className="tempo-chart__bar-container tempo-chart__bar-container--away">
                {awayDataAvailable ? (
                  <>
                    <div
                      className="tempo-chart__bar tempo-chart__bar--away"
                      style={{ width: `${awayWidth}%` }}
                      title={`${awayGoals || 0} goals ${mode}`}
                    />
                    <span className="tempo-chart__pct">{awayPct.toFixed(0)}%</span>
                  </>
                ) : (
                  idx === 0 && <span className="tempo-chart__no-data">No data</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TempoChart({
  homePeriods,
  awayPeriods,
  homeTeamName,
  awayTeamName,
  homeDataAvailable = true,
  awayDataAvailable = true,
}: TempoChartProps) {
  const [mode, setMode] = useState<ChartMode>('scored');

  return (
    <div className="tempo-chart">
      {/* Mobile: Toggle between scored/conceded */}
      <div className="tempo-chart--mobile">
        <div className="tempo-chart__header">
          <span className="tempo-chart__team tempo-chart__team--home">{homeTeamName}</span>
          <div className="tempo-chart__toggle">
            <button
              className={`tempo-chart__toggle-btn ${mode === 'scored' ? 'tempo-chart__toggle-btn--active' : ''}`}
              onClick={() => setMode('scored')}
            >
              Scored
            </button>
            <button
              className={`tempo-chart__toggle-btn ${mode === 'conceded' ? 'tempo-chart__toggle-btn--active' : ''}`}
              onClick={() => setMode('conceded')}
            >
              Conceded
            </button>
          </div>
          <span className="tempo-chart__team tempo-chart__team--away">{awayTeamName}</span>
        </div>
        <SingleChart
          homePeriods={homePeriods}
          awayPeriods={awayPeriods}
          homeTeamName={homeTeamName}
          awayTeamName={awayTeamName}
          homeDataAvailable={homeDataAvailable}
          awayDataAvailable={awayDataAvailable}
          mode={mode}
          showHeader={false}
        />
      </div>

      {/* Desktop: Side-by-side charts */}
      <div className="tempo-chart--desktop">
        <div className="tempo-chart__dual">
          <SingleChart
            homePeriods={homePeriods}
            awayPeriods={awayPeriods}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            homeDataAvailable={homeDataAvailable}
            awayDataAvailable={awayDataAvailable}
            mode="scored"
            showHeader={true}
          />
          <SingleChart
            homePeriods={homePeriods}
            awayPeriods={awayPeriods}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            homeDataAvailable={homeDataAvailable}
            awayDataAvailable={awayDataAvailable}
            mode="conceded"
            showHeader={true}
          />
        </div>
      </div>
    </div>
  );
}

export default TempoChart;
